import { create } from 'zustand';
import type { AppSettings } from '../engine/types';
import { settings as settingsApi } from '../lib/api';
import { useI18n } from '../i18n';

interface SettingsState {
  settings: AppSettings;
  isLoaded: boolean;
  load: () => Promise<void>;
  update: (updates: Partial<AppSettings>) => Promise<void>;
  getUndoLimit: () => number;
  getPricePerFrame: () => number;
  getPricePerRack: () => number;
  isServerEnabled: () => boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  club_name: 'Snooker Club',
  logo_path: '',
  price_per_frame: '20',
  price_per_rack: '15',
  currency: 'MAD',
  undo_limit: '6',
  stats_retention_days: '30',
  stats_keep_forever: '0',
  server_port: '8080',
  server_enabled: '1',
  default_player1_name: 'Player 1',
  default_player2_name: 'Player 2',
  language: 'en',
  audio_enabled: '1',
  color_assist: '0',
  work_start_time: '08:00',
  work_end_time: '07:30',
};

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  isLoaded: false,

  load: async () => {
    const loaded = await settingsApi.getAll();
    // Merge with defaults so any fields the server doesn't know about yet
    // (e.g. newly added keys) always have a valid fallback value.
    const merged: AppSettings = { ...DEFAULT_SETTINGS, ...loaded };
    const lang = merged.language as 'en' | 'fr';
    if (lang === 'en' || lang === 'fr') useI18n.getState().setLanguage(lang);
    set({ settings: merged, isLoaded: true });
  },

  update: async (updates) => {
    const updated = await settingsApi.update(updates);
    const merged: AppSettings = { ...DEFAULT_SETTINGS, ...updated };
    if (updates.language) {
      const lang = updates.language as 'en' | 'fr';
      if (lang === 'en' || lang === 'fr') useI18n.getState().setLanguage(lang);
    }
    set({ settings: merged });
  },

  getUndoLimit: () => {
    const n = parseInt(get().settings.undo_limit, 10);
    return isNaN(n) ? 6 : n;
  },

  getPricePerFrame: () => {
    const n = parseFloat(get().settings.price_per_frame);
    return isNaN(n) ? 20 : n;
  },

  getPricePerRack: () => {
    const n = parseFloat(get().settings.price_per_rack);
    return isNaN(n) ? 15 : n;
  },

  isServerEnabled: () => get().settings.server_enabled === '1',
}));
